import { ApolloError, useApolloClient, useMutation } from "@apollo/client";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LOGIN_MUTATION, {
  GET_USER_DATA,
  LOGOUT,
  REFRESH_TOKEN,
  REGISTER_USER,
} from "../queries/mutation/login_query";
import AuthUser from "../types/Auth";

interface AuthContextType {
  user: AuthUser | null;
  register: (
    username: string,
    password: string,
    recaptchaToken: string,
    dateOfBirth: string
  ) => Promise<boolean>;
  login: (
    username: string,
    password: string,
    recaptchaToken: string
  ) => Promise<AuthUser | null>;
  logout: () => void;
  isAuthenticated: boolean;
  token: string | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const client = useApolloClient();

  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token")
  );
  const [isLoading, setIsLoading] = useState(true); // Track if user data is still loading

  const handleError = (error: ApolloError) => {
    console.error(error);
    toast.error(error.message);
  };

  const [loginMutation] = useMutation(LOGIN_MUTATION, { onError: handleError });
  const [logoutMutation] = useMutation(LOGOUT, { onError: handleError });

  const [registerUser] = useMutation(REGISTER_USER, { onError: handleError });
  const [refreshToken] = useMutation(REFRESH_TOKEN, {
    fetchPolicy: "no-cache",
    onError: handleError,
  });

  // Refresh token on mount if available
  useEffect(() => {
    if (localStorage.getItem("token")) {
      refreshAccessToken();
    } else {
      setIsLoading(false);
    }
  }, []);

  const refreshAccessToken = async () => {
    try {
      const { data } = await refreshToken();
      const newAccessToken = data?.refreshToken;
      if (newAccessToken) {
        localStorage.setItem("token", newAccessToken);
        setToken(newAccessToken);
        const { data } = await client.query({
          query: GET_USER_DATA,
          fetchPolicy: "no-cache", // Ensures fresh data
        });
        setUser(data.getUserData);
        setIsLoading(false);
        //navigateHome(data.getUserData);
      } else {
        logout();
      }
    } catch (error) {
      console.error("Failed to refresh token", error);
      logout();
    }
  };

  const login = async (
    username: string,
    password: string,
    recaptchaToken: string
  ) => {
    try {
      const { data } = await loginMutation({
        variables: { userInput: { username, password, recaptchaToken } },
      });

      if (data?.login?.accessToken) {
        localStorage.setItem("token", data.login.accessToken);
        setUser(data.login);
        setToken(data.login.accessToken);
        navigateHome(data.login);
        setIsLoading(false);

        return data.login;
      }
    } catch (error) {
      console.error("Login error:", error);
    }
    return null;
  };

  const register = async (
    username: string,
    password: string,
    recaptchaToken: string,
    dateOfBirth: string
  ) => {
    try {
      const { data } = await registerUser({
        variables: {
          userInput: { recaptchaToken, username, password, dateOfBirth },
        },
      });

      if (data?.registerUser?.accessToken) {
        localStorage.setItem("token", data.registerUser.accessToken);
        setToken(data.registerUser.accessToken);
        setUser(data.registerUser);
        navigateHome(data.registerUser);
        return true;
      }
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Something went wrong with registering"
      );
    }
    return false;
  };

  const logout = async () => {
    const res = await logoutMutation();
    localStorage.removeItem("token");
    setUser(null);
    setToken(null);
    navigate("/login");
    await client.clearStore(); // Clears the cache
  };

  const navigateHome = (user: AuthUser) => {
    const route = user.isBroadcaster ? "/" : "/home";
    navigate(route);
  };
  const isAuthenticated = !!user; // Derived value

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated,
        register,
        token,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use authentication context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
